import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { globalFeatureFlags, type GlobalFeatureFlags } from '@/lib/featureFlags';

interface FeatureFlagContextValue {
  isEnabled: (flag: keyof GlobalFeatureFlags) => boolean;
  refresh: () => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  
  const refresh = () => {
    const allFlags = globalFeatureFlags.getAllFlags();
    const flagStates: Record<string, boolean> = {};
    Object.keys(allFlags).forEach(key => {
      flagStates[key] = globalFeatureFlags.isEnabled(key as keyof GlobalFeatureFlags) || 
                        globalFeatureFlags.isInCanary(key as keyof GlobalFeatureFlags);
    });
    setFlags(flagStates);
  };
  
  useEffect(() => {
    refresh(); // Initial load
    
    // Listen for custom events from admin panel
    const handleFlagChange = () => refresh();
    window.addEventListener('featureFlagChanged', handleFlagChange);
    
    return () => window.removeEventListener('featureFlagChanged', handleFlagChange);
  }, []);
  
  const isEnabled = (flag: keyof GlobalFeatureFlags) => {
    return flags[flag] ?? (
      globalFeatureFlags.isEnabled(flag) || globalFeatureFlags.isInCanary(flag)
    );
  };
  
  return (
    <FeatureFlagContext.Provider value={{ isEnabled, refresh }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useGlobalFeatureFlag = (flag: keyof GlobalFeatureFlags): boolean => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    // Fallback if context not available (e.g., in tests or outside provider)
    return globalFeatureFlags.isEnabled(flag) || globalFeatureFlags.isInCanary(flag);
  }
  return context.isEnabled(flag);
};
